/**
 * Renders review shapes as SVG. Shapes are stored as 0–1 fractions of the
 * annotated viewport; this component maps them into whatever box it is given,
 * so the same data draws correctly over the live screen (full viewport) and
 * over the stored screenshot in the Support Hub (arbitrary size).
 */
import React from 'react';
import type { ReviewShape } from '../../types/screenReview.types';

const STROKE = '#e11d48'; // rose-600 — must stand out on the green-brand UI.

interface Props {
  shapes: ReviewShape[];
  /**
   * Coordinate space of the drawing surface. With `responsive`, this is only
   * the aspect ratio — the SVG stretches to fill its container (used in the
   * Support Hub to replay shapes over a scaled screenshot).
   */
  width: number;
  height: number;
  responsive?: boolean;
}

export const ShapeRenderer: React.FC<Props> = ({ shapes, width, height, responsive }) => {
  // Namespaced per instance — several renderers coexist on the Support Hub
  // page, and duplicate marker ids would all resolve to the first in the DOM.
  const markerId = `review-arrowhead-${React.useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

  if (width <= 0 || height <= 0) return null;
  const X = (v: number): number => v * width;
  const Y = (v: number): number => v * height;

  return (
    <svg
      {...(responsive ? {} : { width, height })}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className={responsive ? 'absolute inset-0 h-full w-full' : 'absolute inset-0'}
      style={{ pointerEvents: 'none' }}
      aria-hidden="true"
    >
      <defs>
        <marker
          id={markerId}
          markerWidth="10"
          markerHeight="8"
          refX="9"
          refY="4"
          orient="auto"
        >
          <path d="M0,0 L10,4 L0,8 Z" fill={STROKE} />
        </marker>
      </defs>
      {shapes.map((s, i) => {
        switch (s.kind) {
          case 'pin':
            return (
              <g key={i}>
                <circle cx={X(s.x)} cy={Y(s.y)} r={12} fill={STROKE} opacity={0.25} />
                <circle cx={X(s.x)} cy={Y(s.y)} r={5} fill={STROKE} />
              </g>
            );
          case 'arrow':
            return (
              <line
                key={i}
                x1={X(s.x1)}
                y1={Y(s.y1)}
                x2={X(s.x2)}
                y2={Y(s.y2)}
                stroke={STROKE}
                strokeWidth={3}
                markerEnd={`url(#${markerId})`}
              />
            );
          case 'rect':
            return (
              <rect
                key={i}
                x={X(s.x)}
                y={Y(s.y)}
                width={X(s.w)}
                height={Y(s.h)}
                fill="none"
                stroke={STROKE}
                strokeWidth={3}
                rx={6}
              />
            );
          case 'redact':
            // Solid — hides content on the live screen; also burned into the
            // stored JPEG by captureScreenshot, so this is belt-and-braces.
            return (
              <rect
                key={i}
                x={X(s.x)}
                y={Y(s.y)}
                width={X(s.w)}
                height={Y(s.h)}
                fill="#111827"
                stroke="#111827"
                strokeWidth={1}
                rx={2}
              />
            );
          case 'freehand':
            return (
              <polyline
                key={i}
                points={s.points.map(([px, py]) => `${X(px)},${Y(py)}`).join(' ')}
                fill="none"
                stroke={STROKE}
                strokeWidth={3}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            );
          case 'text':
            return (
              <g key={i}>
                {/* Halo so the label survives any background. */}
                <text
                  x={X(s.x)}
                  y={Y(s.y)}
                  fontSize={15}
                  fontWeight={700}
                  stroke="#ffffff"
                  strokeWidth={4}
                  paintOrder="stroke"
                  fill={STROKE}
                >
                  {s.text}
                </text>
              </g>
            );
          default:
            return null;
        }
      })}
    </svg>
  );
};

export default ShapeRenderer;
